import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Brain, Tag, Trash2 } from 'lucide-react';
import { apiService } from '../services/api';
import { toast } from 'sonner';
import TESTIDS from '../constants/testIds';

interface Memory {
  id: string;
  title: string;
  content: string;
  type: string;
  tags: string[];
  usage_count: number;
  created_at: string;
}

interface MemoryPanelProps {
  workspaceId: string;
}

const MemoryPanel = ({ workspaceId }: MemoryPanelProps) => {
  const [memories, setMemories] = useState<Memory[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newMemory, setNewMemory] = useState({
    title: '',
    content: '',
    type: 'pattern' as 'pattern' | 'convention' | 'decision' | 'solution' | 'error',
    tags: '',
  });

  const loadMemories = React.useCallback(async () => {
    try {
      const data = await apiService.listMemories(workspaceId);
      setMemories(data);
    } catch (error) {
      toast.error('Error al cargar memoria');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    loadMemories();
  }, [loadMemories]);

  const handleCreateMemory = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);

    try {
      const memory = await apiService.createMemory({
        workspace_id: workspaceId,
        title: newMemory.title,
        content: newMemory.content,
        type: newMemory.type,
        tags: newMemory.tags.split(',').map((t) => t.trim()).filter(Boolean),
        metadata: {},
      });
      toast.success('Memoria creada exitosamente');
      setMemories([memory, ...memories]);
      setIsDialogOpen(false);
      setNewMemory({ title: '', content: '', type: 'pattern', tags: '' });
    } catch (error) {
      toast.error('Error al crear memoria');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteMemory = async (memoryId: string) => {
    if (!window.confirm('¿Eliminar esta memoria?')) return;

    try {
      await apiService.deleteMemory(memoryId);
      toast.success('Memoria eliminada');
      setMemories(memories.filter((m) => m.id !== memoryId));
    } catch (error) {
      toast.error('Error al eliminar memoria');
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'pattern': return 'text-[#3B82F6]';
      case 'convention': return 'text-[#10B981]';
      case 'decision': return 'text-[#F59E0B]';
      case 'solution': return 'text-[#8B5CF6]';
      case 'error': return 'text-[#FF3B30]';
      default: return 'text-[#A1A1AA]';
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-medium tracking-tight text-white mb-1">Memoria del Workspace</h2>
          <p className="text-[#A1A1AA] text-sm">Conocimiento reutilizable: patrones, convenciones, decisiones y soluciones</p>
        </div>

        <button
          onClick={() => setIsDialogOpen(true)}
          data-testid={TESTIDS.MEMORY_CREATE_BUTTON}
          className="bg-white text-black font-medium hover:bg-neutral-200 transition-colors rounded-md px-4 py-2 flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nueva Memoria
        </button>
      </div>

      {/* Modal */}
      {isDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setIsDialogOpen(false)}>
          <div className="bg-[#0E0F11] border border-white/10 rounded-xl p-6 max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-2xl font-medium tracking-tight text-white mb-4">Crear Memoria</h3>
            <form onSubmit={handleCreateMemory} className="space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#A1A1AA] mb-2 block">Tipo</label>
                <select
                  value={newMemory.type}
                  onChange={(e: any) => setNewMemory({ ...newMemory, type: e.target.value })}
                  data-testid={TESTIDS.MEMORY_TYPE_SELECT}
                  className="w-full bg-[#050505] border border-white/10 text-white rounded-md px-3 py-2"
                >
                  <option value="pattern">Patrón - Patrón de diseño o arquitectura</option>
                  <option value="convention">Convención - Regla o estándar</option>
                  <option value="decision">Decisión - Decisión técnica</option>
                  <option value="solution">Solución - Solución a problema</option>
                  <option value="error">Error - Error conocido y solución</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#A1A1AA] mb-2 block">Título</label>
                <input
                  type="text"
                  value={newMemory.title}
                  onChange={(e) => setNewMemory({ ...newMemory, title: e.target.value })}
                  placeholder="Título descriptivo"
                  required
                  data-testid={TESTIDS.MEMORY_TITLE_INPUT}
                  className="w-full bg-[#050505] border border-white/10 text-white placeholder:text-zinc-600 rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#A1A1AA] mb-2 block">Contenido</label>
                <textarea
                  value={newMemory.content}
                  onChange={(e) => setNewMemory({ ...newMemory, content: e.target.value })}
                  placeholder="Describe el conocimiento que quieres almacenar..."
                  required
                  data-testid={TESTIDS.MEMORY_CONTENT_INPUT}
                  rows={6}
                  className="w-full bg-[#050505] border border-white/10 text-white placeholder:text-zinc-600 rounded-md px-3 py-2 resize-none"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#A1A1AA] mb-2 block">Tags (separados por coma)</label>
                <input
                  type="text"
                  value={newMemory.tags}
                  onChange={(e) => setNewMemory({ ...newMemory, tags: e.target.value })}
                  placeholder="react, api, backend"
                  className="w-full bg-[#050505] border border-white/10 text-white placeholder:text-zinc-600 rounded-md px-3 py-2"
                />
              </div>
              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 bg-white text-black font-medium hover:bg-neutral-200 transition-colors rounded-md py-3"
                >
                  {creating ? 'Guardando...' : 'Guardar Memoria'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsDialogOpen(false)}
                  className="px-6 bg-transparent border border-white/10 text-white hover:bg-white/5 transition-colors rounded-md"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" style={{ width: '40px', height: '40px' }} />
        </div>
      ) : memories.length === 0 ? (
        <div className="bg-[#0E0F11] border border-white/5 p-12 rounded-xl text-center">
          <Brain className="w-16 h-16 text-[#52525B] mx-auto mb-4" strokeWidth={1.5} />
          <p className="text-[#A1A1AA] text-lg mb-2">No hay memoria almacenada</p>
          <p className="text-[#52525B] text-sm">El sistema aprenderá automáticamente o puedes agregar conocimiento manualmente</p>
        </div>
      ) : (
        <div className="space-y-4">
          {memories.map((memory, index) => (
            <motion.div key={memory.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}>
              <div data-testid={TESTIDS.MEMORY_CARD} className="bg-[#0E0F11] border border-white/5 p-6 rounded-xl hover:border-white/10 transition-all group">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <span className={`text-xs font-mono uppercase font-bold ${getTypeColor(memory.type)}`}>{memory.type}</span>
                    <h3 className="text-white text-lg font-medium tracking-tight mt-1">{memory.title}</h3>
                  </div>
                  <button
                    onClick={() => handleDeleteMemory(memory.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-[#FF3B30] hover:bg-[#FF3B30]/10 p-2 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-[#A1A1AA] text-base leading-relaxed mb-3">{memory.content}</p>
                {memory.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {memory.tags.map((tag, i) => (
                      <span key={i} className="inline-flex items-center px-2 py-1 bg-white/5 border border-white/10 rounded-full text-xs text-[#A1A1AA] font-mono">
                        <Tag className="w-3 h-3 mr-1" />{tag}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                  <span className="text-[#52525B] text-xs font-mono">Usado {memory.usage_count} {memory.usage_count === 1 ? 'vez' : 'veces'}</span>
                  <span className="text-[#52525B] text-xs font-mono">{new Date(memory.created_at).toLocaleDateString('es-ES')}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MemoryPanel;